import {useMemo} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useDispatch} from "react-redux";
import {AxiosError} from "axios";

import {useAuthUserId} from '../../../selectors/AuthSelectors';
import {useRestaurantId} from '../../../selectors/RestaurantSelectors';
import {customErrorShackBar, customMessageShackBar} from "../../../actions/snackbars/SnackBarsActions";

import {
  getGuestAcquisitionOffersRequest,
  getGuestAcquisitionRestaurantChannelsRequest,
  getGuestAcquisitionRestaurantSettingsRequest,
  getGuestAcquisitionAcceptOfferRequest, getGuestAcquisitionSetRestaurantSettingsRequest,
} from '../../../api';

import {sortOffers} from "../lib";


export function useOffersQueryKey() {
  return [useAuthUserId(), 'guest_acquisition', 'offers'];
}

export function useOffers() {
  const restaurantId = useRestaurantId();

  const { data: response, isLoading, isFetching, isFetched } = useQuery({
    // @todo Вынести время кэша в настройки?
    staleTime: 60 * 1000 * 30,
    queryKey: useOffersQueryKey(),
    queryFn: () => getGuestAcquisitionOffersRequest(),
    keepPreviousData: true,
  });

  const offers = useMemo<Array<GM.GuestAcquisition.OfferDTO> | null>(() => {
    if (!response || !response.data) {
      return null;
    }

    return sortOffers(response.data);
  }, [response?.data]);

  return {
    isFetched,
    isLoading: isLoading || isFetching,
    data: offers,
  };
}

export function useLatestOffer() {
  const offers = useOffers();

  return useMemo<GM.GuestAcquisition.OfferDTO | null>(() => {
    if (!offers.data || offers.data.length === 0) {
      return null;
    }

    return offers.data[0];
  }, [offers.data]);
}

export function useRestaurantSettingsQueryKey() {
  return [useAuthUserId(), 'guest_acquisition', 'restaurant_settings', useRestaurantId()];
}

export function useRestaurantSettings() {
  const restaurantId = useRestaurantId();

  const { data: response, isLoading, isFetching, isFetched } = useQuery({
    // @todo Вынести время кэша в настройки?
    staleTime: 60 * 1000 * 10,
    enabled: !!restaurantId,
    queryKey: useRestaurantSettingsQueryKey(),
    queryFn: () => getGuestAcquisitionRestaurantSettingsRequest(restaurantId),
    keepPreviousData: true,
  });

  return {
    isFetched,
    isLoading: isLoading || isFetching,
    data: (response && response.data) ? response.data : null,
  };
}

export function useRestaurantChannelsQueryKey() {
  return [useAuthUserId(), 'guest_acquisition', 'restaurant_channels', useRestaurantId()];
}

export function useRestaurantChannels() {
  const restaurantId = useRestaurantId();

  const { data: response, isLoading, isFetching, isFetched } = useQuery({
    // @todo Вынести время кэша в настройки?
    staleTime: 60 * 1000 * 10,
    enabled: !!restaurantId,
    queryKey: useRestaurantChannelsQueryKey(),
    queryFn: () => getGuestAcquisitionRestaurantChannelsRequest(restaurantId),
    keepPreviousData: true,
  });

  return {
    isFetched,
    isLoading: isLoading || isFetching,
    data: (response && response.data) ? response.data : null,
  };
}

export function useAcceptOfferMutation() {
  const queryClient = useQueryClient();
  const queryKeyToReset = useRestaurantSettingsQueryKey();
  const dispatch = useDispatch();

  return useMutation<GM.GuestAcquisition.Response.AcceptOffer, AxiosError, any>({
    mutationFn: (payload: { restaurantId: number }) => {
      const { restaurantId } = payload;

      return getGuestAcquisitionAcceptOfferRequest(restaurantId);
    },

    onSuccess: (response: any) => {
      if (response.status === 200) {
        // dispatch(customMessageShackBar(locales.t('employees.actions.create.success')));
        dispatch(customMessageShackBar('Оферта принята'));
        queryClient.invalidateQueries({ queryKey: queryKeyToReset });
      } else {
        // dispatch(customErrorShackBar(locales.t('employees.actions.create.error')));
        dispatch(customErrorShackBar('Ошибка при обновлении'));
      }
    },

    onError: (error) => {
      dispatch(customErrorShackBar('Ошибка при обновлении'));
    },
  });
}

export function useSetRestaurantChannelsMutation() {
  const queryClient = useQueryClient();
  const queryKeyToReset = useRestaurantSettingsQueryKey();
  const dispatch = useDispatch();

  return useMutation<GM.GuestAcquisition.Response.SetRestaurantChannels, AxiosError, any>({
    mutationFn: (payload: {
      restaurantId: number,
      channels: Array<{channel: GM.GuestAcquisition.Channel}>,
    }) => {
      const { restaurantId, channels } = payload;

      return getGuestAcquisitionSetRestaurantSettingsRequest(restaurantId, {channels});
    },

    onSuccess: (response: any) => {
      if (response.status === 200) {
        // dispatch(customMessageShackBar(locales.t('employees.actions.create.success')));
        dispatch(customMessageShackBar('Источники обновлены'));
        queryClient.invalidateQueries({ queryKey: queryKeyToReset });
      } else {
        // dispatch(customErrorShackBar(locales.t('employees.actions.create.error')));
        dispatch(customErrorShackBar('Ошибка при обновлении'));
      }
    },

    onError: (error) => {
      // dispatch(customErrorShackBar(locales.t('employees.actions.create.error')));
      dispatch(customErrorShackBar('Ошибка при обновлении'));
    },
  });
}
